import React, { useState } from 'react';
import { ICellRendererParams } from '@ag-grid-enterprise/all-modules';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBug, faCheck, faMoon, faPencilAlt, faRedo, faSpinner, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { BG_COLOR, DarkMode } from '../../../modules/AIIllustration/types/darkMode';
import ModuleContext from 'CMFW/context/ModuleContext';
import { AIIllustrationItemsModel } from 'models/AIIllustrationItemsModel';
import { AIIllustrationItemStatus } from 'types/aiIllustration';
import { ImageViewerModal } from '../../../modules/AIIllustration/components/ImageViewerModal';
import { EditImageModal } from '../../../modules/AIIllustration/components/EditImageModal';
import { FaceFixModal } from '../../../modules/AIIllustration/components/FaceFixModal';

const PngImageRendererWithActions: React.FC<ICellRendererParams> = (params) => {
  const { value, data, colDef } = params;
  const itemsModel = React.useContext(ModuleContext) as AIIllustrationItemsModel;
  const [isHovering, setIsHovering] = useState(false);
  const [darkMode, setDarkMode] = useState(DarkMode.LIGHT);

  const toggleDarkMode = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDarkMode((d) => (d === DarkMode.LIGHT ? DarkMode.DARK : DarkMode.LIGHT));
  };

  // Modal states
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFaceFixModal, setShowFaceFixModal] = useState(false);

  // Determine which alternative this is based on the column field
  const fieldName = colDef?.field as 'alternative_1' | 'alternative_2';
  const alternativeNumber = fieldName === 'alternative_1' ? 1 : 2;

  // Must have row data
  if (!data) {
    return null;
  }

  // Check if this alternative has an error
  const isError = data?.status === AIIllustrationItemStatus.ERROR;

  // Check if this specific alternative is being processed
  const isProcessing = data?.processing_alternatives?.includes(alternativeNumber) ?? false;

  // Check if this specific alternative is being approved
  const isApproving = itemsModel.isApprovingAlternative(data.id, alternativeNumber);

  // Check if item is done (approved)
  const isDone = data?.status === AIIllustrationItemStatus.DONE;

  // Check if this alternative was the one that was approved
  const approvedAlternative = data?.enhanced_results?.approved_alternative;
  const isApprovedAlternative = isDone && approvedAlternative === alternativeNumber;
  const isNotApprovedAlternative =
    isDone && approvedAlternative !== undefined && approvedAlternative !== alternativeNumber;

  // Only return null if no image AND no error AND not processing AND not approving
  if (!value && !isError && !isProcessing && !isApproving) {
    return null;
  }

  const imageType = fieldName;

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Only open viewer if there's an image to view
    if (value) {
      setShowImageViewer(true);
    }
  };

  const handleEdit = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isProcessing) return; // Prevent action if already processing
    setShowEditModal(true);
  };

  const handleRedo = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isProcessing) return; // Prevent action if already processing
    try {
      await itemsModel.redoItem(data.id, alternativeNumber);
      console.log(`Redo process started for Alternative ${alternativeNumber}`);
      itemsModel.render();
    } catch (error) {
      console.error(`Failed to start redo process: ${error instanceof Error ? error.message : 'Unknown error'}`);
      alert(`Failed to start redo process: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleRetry = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isProcessing) return; // Prevent action if already processing
    try {
      await itemsModel.redoItem(data.id, alternativeNumber);
      console.log(`Retry process started for Alternative ${alternativeNumber}`);
      itemsModel.render();
    } catch (error) {
      console.error(`Failed to start retry process: ${error instanceof Error ? error.message : 'Unknown error'}`);
      alert(`Failed to start retry process: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleValidate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isProcessing || isApproving || isDone || !value) return;

    try {
      await itemsModel.approveItem(data.id, alternativeNumber);
      console.log(`Alternative ${alternativeNumber} approved successfully`);
    } catch (error) {
      console.error(`Failed to approve: ${error instanceof Error ? error.message : 'Unknown error'}`);
      alert(`Failed to approve: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleRemixSubmit = async (
    itemId: number,
    alternativeNumber: number,
    additionalPrompt: string,
    useEnhancedBase: boolean,
    flattenBgColor: string | null,
  ) => {
    try {
      await itemsModel.remixItem(itemId, alternativeNumber, additionalPrompt, useEnhancedBase, flattenBgColor);
      console.log(`Remix process started for Alternative ${alternativeNumber}`);
      itemsModel.render();
    } catch (error) {
      throw error; // Let EditImageModal handle the error
    }
  };

  const handleFixFaces = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isProcessing || !value || !data.original_png) return;
    setShowFaceFixModal(true);
  };

  const handleFaceFixSubmit = async (
    itemId: number,
    alternativeNumber: number,
    maskBase64: string,
    deltaX: number,
    deltaY: number,
  ) => {
    await itemsModel.fixFaces(itemId, alternativeNumber, maskBase64, deltaX, deltaY);
  };

  return (
    <>
      <Container
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onClick={handleImageClick}
        $isNotApproved={isNotApprovedAlternative}
        $bgColor={BG_COLOR[darkMode]}
      >
        {value && (
          <StyledImage
            src={value}
            alt="PNG Image"
            $isNotApproved={isNotApprovedAlternative}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              if (target.parentElement) {
                target.parentElement.innerHTML = "<span style='color: #999;'>Image not found</span>";
              }
            }}
          />
        )}

        {/* Dark mode toggle - bottom right, visible on hover */}
        {value && isHovering && !isProcessing && !isApproving && !isError && (
          <DarkModeToggle onClick={toggleDarkMode} title="Toggle light/dark background">
            <FontAwesomeIcon icon={faMoon} />
          </DarkModeToggle>
        )}

        {/* Error overlay when this alternative has failed */}
        {isError && !isProcessing && (
          <ErrorOverlay>
            <ErrorIcon>
              <FontAwesomeIcon icon={faBug} size="2x" />
            </ErrorIcon>
            <ErrorTitle>Processing Failed</ErrorTitle>
            {data.error_message && (
              <ErrorMessage title={data.error_message}>
                {data.error_message.length > 100 ? `${data.error_message.substring(0, 100)}...` : data.error_message}
              </ErrorMessage>
            )}
            <ErrorActions>
              <ErrorButton
                onClick={handleRetry}
                $variant="retry"
                title="Retry with same parameters"
                disabled={isProcessing}
                style={{ opacity: isProcessing ? 0.5 : 1, cursor: isProcessing ? 'not-allowed' : 'pointer' }}
              >
                <FontAwesomeIcon icon={faRedo} /> Retry
              </ErrorButton>
              {/* Only show Remix button if an image was already generated */}
              {value && (
                <ErrorButton
                  onClick={handleEdit}
                  $variant="remix"
                  title="Retry with modified prompt"
                  disabled={isProcessing}
                  style={{ opacity: isProcessing ? 0.5 : 1, cursor: isProcessing ? 'not-allowed' : 'pointer' }}
                >
                  <FontAwesomeIcon icon={faPencilAlt} /> Remix
                </ErrorButton>
              )}
            </ErrorActions>
          </ErrorOverlay>
        )}

        {/* Loading overlay when this alternative is being processed */}
        {isProcessing && (
          <LoadingOverlay>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <LoadingText>Processing...</LoadingText>
          </LoadingOverlay>
        )}

        {/* Loading overlay when this alternative is being approved */}
        {isApproving && (
          <LoadingOverlay $variant="approve">
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            <LoadingText $variant="approve">Approving...</LoadingText>
          </LoadingOverlay>
        )}

        {/* Action buttons in top-right corner - only visible on hover and not processing/error */}
        {isHovering && !isProcessing && !isApproving && !isError && !isDone && (
          <ActionButtonsContainer>
            <ActionButton onClick={handleValidate} $variant="validate" title="Validate">
              <FontAwesomeIcon icon={faCheck} />
            </ActionButton>
            <ActionButton onClick={handleEdit} $variant="edit" title="Edit/Remix">
              <FontAwesomeIcon icon={faPencilAlt} />
            </ActionButton>
            <ActionButton onClick={handleRedo} $variant="redo" title="Redo">
              <FontAwesomeIcon icon={faRedo} />
            </ActionButton>
            <ActionButton onClick={handleFixFaces} $variant="fixFaces" title="Fix faces">
              <FontAwesomeIcon icon={faUserCircle} />
            </ActionButton>
          </ActionButtonsContainer>
        )}

        {/* Redo/Remix buttons for approved alternative - allows user to change their mind */}
        {isHovering && !isProcessing && !isApproving && !isError && isApprovedAlternative && (
          <ActionButtonsContainer>
            <ActionButton onClick={handleEdit} $variant="edit" title="Edit/Remix">
              <FontAwesomeIcon icon={faPencilAlt} />
            </ActionButton>
            <ActionButton onClick={handleRedo} $variant="redo" title="Redo">
              <FontAwesomeIcon icon={faRedo} />
            </ActionButton>
          </ActionButtonsContainer>
        )}
      </Container>

      {/* Image Viewer Modal */}
      {showImageViewer && value && (
        <ImageViewerModal
          imageUrl={value}
          vocId={data.voc_id}
          vocTitle={data.voc_title}
          imageType={imageType}
          onClose={() => setShowImageViewer(false)}
        />
      )}

      {/* Edit/Remix Modal */}
      {showEditModal && (
        <EditImageModal
          imageUrl={value || ''}
          originalImageUrl={data.original_png || null}
          vocId={data.voc_id}
          vocTitle={data.voc_title}
          itemId={data.id}
          alternativeNumber={alternativeNumber}
          hasAlpha={!!data.original_png}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleRemixSubmit}
        />
      )}

      {/* Face Fix Modal */}
      {showFaceFixModal && value && data.original_png && (
        <FaceFixModal
          enhancedImageUrl={value}
          originalImageUrl={data.original_png}
          itemId={data.id}
          alternativeNumber={alternativeNumber}
          onClose={() => setShowFaceFixModal(false)}
          onSubmit={handleFaceFixSubmit}
        />
      )}
    </>
  );
};

export default PngImageRendererWithActions;

const Container = styled.div<{ $isNotApproved?: boolean; $bgColor: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  height: 200px;
  width: 100%;
  min-width: 250px;
  position: relative;
  cursor: pointer;
  opacity: ${(p) => (p.$isNotApproved ? 0.4 : 1)};
  background-color: ${(p) => p.$bgColor};
  transition: opacity 0.3s ease, background-color 0.2s ease;
`;

const DarkModeToggle = styled.button`
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  padding: 0;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  cursor: pointer;
  color: #607d8b;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 5;

  &:hover {
    color: #455a64;
    background-color: #fff;
    transform: scale(1.1);
  }
`;

const StyledImage = styled.img<{ $isNotApproved?: boolean }>`
  max-width: 250px;
  max-height: 190px;
  width: auto;
  height: auto;
  object-fit: contain;
  filter: ${(p) => (p.$isNotApproved ? 'grayscale(50%)' : 'none')};
  transition: filter 0.3s ease;
`;

const ActionButtonsContainer = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const variantColors: Record<string, string> = {
  validate: '#4CAF50',
  edit: '#2196F3',
  redo: '#FF9800',
  fixFaces: '#9C27B0',
};

const ActionButton = styled.button<{ $variant: string }>`
  width: 32px;
  height: 32px;
  padding: 0;
  background-color: ${(p) => variantColors[p.$variant] || '#757575'};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    opacity: 0.9;
    transform: scale(1.1);
  }

  &:active {
    transform: scale(1.05);
  }
`;

const LoadingOverlay = styled.div<{ $variant?: 'approve' }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${(p) => (p.$variant === 'approve' ? 'rgba(232, 245, 233, 0.95)' : 'rgba(255, 255, 255, 0.9)')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: ${(p) => (p.$variant === 'approve' ? '#4CAF50' : '#2196f3')};
  z-index: 10;
  pointer-events: none;
`;

const LoadingText = styled.div<{ $variant?: 'approve' }>`
  font-size: 14px;
  font-weight: 600;
  color: ${(p) => (p.$variant === 'approve' ? '#4CAF50' : '#2196f3')};
`;

const ErrorOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 235, 238, 0.98);
  border: 2px solid #ef5350;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #c62828;
  z-index: 20;
  padding: 16px;
`;

const ErrorIcon = styled.div`
  color: #ef5350;
`;

const ErrorTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #c62828;
`;

const ErrorMessage = styled.div`
  font-size: 12px;
  color: #d32f2f;
  text-align: center;
  max-width: 90%;
  line-height: 1.4;
  background: rgba(255, 255, 255, 0.7);
  padding: 8px;
  border-radius: 4px;
`;

const ErrorActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const ErrorButton = styled.button<{ $variant: 'retry' | 'remix' }>`
  padding: 8px 16px;
  background-color: ${(p) => (p.$variant === 'retry' ? '#ef5350' : '#42a5f5')};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background-color: ${(p) => (p.$variant === 'retry' ? '#e53935' : '#1e88e5')};
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.25);
  }

  &:active {
    transform: translateY(0);
  }
`;
